import { Close, Plus } from 'assets/icons';
import AddressGeoApi from 'components/AddressGeoApi/AddressGeoApi';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import {
  agreementsOf,
  diffCapital,
  draftFromCompany,
  isCapitalValid,
  statusOptionsOf,
} from 'components/companyForm/companyLegalForm';
import JustificatifConfirmation, { CONFIRMATION_TITLE } from 'components/justificatifConfirmation/JustificatifConfirmation';
import useModal from 'components/modal/useModal';
import { SkeletonField } from 'components/skeleton/Skeleton';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import FieldLabel from 'components/styledInput/FieldLabel';
import PillTabs from 'components/tabs/PillTabs';
import StyledInput from 'components/styledInput/StyledInput';
import TextSelectMultipleBigAccount from 'components/textSelectMultiple/textSelectMultipleBigAccount';
import { buildCompanySupplierFormOptions } from 'constants/missionSupplierFilters';
import { CLIENT_SIZES, clientSizeLabel } from 'constants/clientSizes';
import { commercialsOf } from 'components/companies/companyAssignees';
import {
  diffCompanyOverview,
  isCompanyOverviewValid,
  normalizePennylaneCategoryId,
  validateCompanyOverview,
} from 'helper/companyOverviewDiff';
import { isMail } from 'components/mail/scheduleSlots';
import { toPhoneValue } from 'helper/companyContactPhone';
import { callApi } from 'helper/promises';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { apiErrorText } from 'helper/apiErrorText';
import { useNotifyCompaniesChanged } from 'hooks/useCompaniesChanged';
import UseContext from 'hooks/useContext';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { isValidPhoneNumber } from 'react-phone-number-input';
import ApiDatabase from 'server';
import crmApi from 'server/crm/crmApi';

export const EDIT_COMPANY_PANEL = 'edit_company';

const sameCodes = (one, other) => one.length === other.length && one.every((code) => other.includes(code));
const quiet = (err) => {
  if (err?.name !== 'CanceledError') console.error(err);
};

// The form's tabs: a setting found by its part, all of them saved at once.
const TABS = [
  { id: 'legal', label: 'Informations légales' },
  { id: 'activity', label: 'Activité' },
  { id: 'installations', label: 'Installations' },
  { id: 'costs', label: 'Estimations de coût' },
];

const Section = ({ children }) => <section className="flex flex-col gap-4">{children}</section>;

/**
 * « Modifier l'entreprise » (slide-over `edit_company`, `objID`: the
 * company): every setting of the company in one form, in place of the read
 * view and its four modals. The legal information, the Pennylane sector, the
 * collective installations and the cost estimates are written as those modals
 * write them (postUpdateCompanyOverview, postCollectiveInstallationsCompany,
 * putDisplayCostEstimate); a change of the legal information passes through
 * the justificatif first (JBWEB-1214). The big account saves as soon as it is
 * picked, as in the activity modal. Its number and its general e-mail are the
 * CRM's (setCompanyPhone, setCompanyEmail), as are its size (setCompanySize,
 * unless it follows its timesheets) and its commercial (assignCompany): no
 * justificatif. The company page opens it with `objID` = { id, phone, email,
 * canEditEmail, size, sizeFromEtp, commercialId }, what it shows.
 */
const CompanyEditPanel = () => {
  const { objID, refreshCompanyBigAccount, setRefreshCompanyBigAccount, modalDataReturn, setModalDataReturn, language } =
    UseContext();
  const opened = objID && typeof objID === 'object' ? objID : { id: objID };
  const companyId = opened.id ? String(opened.id) : null;
  const { call } = useCrmContext();
  const { text } = useText('record');
  const { text: companiesText } = useText('companies');
  const [size, setSize] = useState(opened.size ?? null);
  const [commercialId, setCommercialId] = useState(opened.commercialId ?? null);
  const [commercials, setCommercials] = useState([]);
  useEffect(() => {
    call(crmApi.getTeam).then((team) => setCommercials(commercialsOf(team ?? [])), () => setCommercials([]));
  }, [call]);
  // A prospect's number is filed in the national format: shown as E.164, it keeps
  // its country and is not rewritten unless edited.
  const initialChannels = { phone: toPhoneValue(opened.phone) ?? opened.phone ?? '', email: opened.email ?? '' };
  const [channels, setChannels] = useState(initialChannels);
  const { toggleSlideOver } = useSlideOver();
  const { toggle } = useModal();
  const notifyRecord = useNotifyRecordChanged();
  const notifyCompanies = useNotifyCompaniesChanged();
  const justificatif = useJustificatifConfirmation();
  const token = localStorage.getItem('xsrfToken');

  const [company, setCompany] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [referential, setReferential] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [initialInstalled, setInitialInstalled] = useState([]);
  const [costEstimate, setCostEstimate] = useState(false);
  const [initialCostEstimate, setInitialCostEstimate] = useState(false);
  const [bigAccount, setBigAccount] = useState('');
  // The legal fields being confirmed, frozen on the way into the step.
  const [pendingFields, setPendingFields] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('legal');

  const close = () => toggleSlideOver();

  useEffect(() => {
    if (!companyId) return;
    ApiDatabase.getCompanyInfo(
      { idCompany: companyId },
      (data) => {
        const info = data?.company ?? null;
        if (!info) return setFailed(true);
        setCompany(info);
        setDraft(draftFromCompany(info));
        setCategory(normalizePennylaneCategoryId(info.pennylane_category_id));
        ApiDatabase.getCollectiveAgreement({ token }, (list) => setAgreements(agreementsOf(list, info)), quiet);
      },
      (err) => {
        if (err?.name !== 'CanceledError') setFailed(true);
      },
    );
    ApiDatabase.getCompanySuppliers({}, (data) => setSuppliers(Array.isArray(data) ? data : []), quiet);
    ApiDatabase.getCollectiveInstallations({ token }, (data) => setReferential(data?.installations ?? []), quiet);
    ApiDatabase.getCollectiveInstallationsCompany(
      { token, idCompany: companyId },
      (data) => {
        const codes = (data?.installations ?? []).map((installation) => installation.code);
        setInstalled(codes);
        setInitialInstalled(codes);
      },
      quiet,
    );
    ApiDatabase.getDisplayCostEstimate(
      { token, idCompany: companyId },
      (data) => {
        setCostEstimate(Boolean(data?.displayCostEstimate));
        setInitialCostEstimate(Boolean(data?.displayCostEstimate));
      },
      quiet,
    );
  }, [companyId, token]);

  const loadCategories = () =>
    ApiDatabase.getPennyLaneCategories({ token }, (data) => setCategories(data?.items || []), quiet);
  useEffect(() => {
    loadCategories();
  }, [token]);

  // A category created from the sector's list is read back, and picked.
  useEffect(() => {
    if (!modalDataReturn?.createdCategory) return;
    loadCategories();
    setModalDataReturn({});
  }, [modalDataReturn]);

  useEffect(() => {
    if (!companyId) return;
    ApiDatabase.getCompanyBigAccount({ token, idCompany: companyId }, (data) => setBigAccount(data?.bigAccount ?? ''), quiet);
  }, [companyId, token, refreshCompanyBigAccount]);

  if (failed) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel="Fermer">
          Modifier l’entreprise
        </SlideOverHeader>
        <SlideOverBody>
          <p className="text-sm text-gray-500">Les informations de l’entreprise n’ont pas pu être chargées.</p>
        </SlideOverBody>
      </>
    );
  }

  const ready = Boolean(company && draft);
  const errors = ready
    ? {
        ...validateCompanyOverview(draft),
        errorCapital: isCapitalValid(draft) ? '' : text('capitalInvalid'),
      }
    : {};
  const legalValid = isCompanyOverviewValid(errors) && !errors.errorCapital;
  // The capital is a legal information like the staff: it passes through the justificatif.
  const legalFields = ready
    ? { ...diffCompanyOverview(draft, company), ...diffCapital(draft, company) }
    : {};
  const legalChanged = Object.keys(legalFields).length > 0;
  const categoryChanged =
    ready && normalizePennylaneCategoryId(category) !== normalizePennylaneCategoryId(company.pennylane_category_id);
  const installationsChanged = !sameCodes(installed, initialInstalled);
  const costChanged = costEstimate !== initialCostEstimate;
  const phoneChanged = channels.phone.trim() !== initialChannels.phone;
  const emailChanged = channels.email.trim().toLowerCase() !== initialChannels.email.toLowerCase();
  const sizeChanged = !opened.sizeFromEtp && size !== (opened.size ?? null);
  const commercialChanged = Boolean(commercialId) && commercialId !== (opened.commercialId ?? null);
  const channelErrors = {
    // A stored number left as is never blocks saving the other fields.
    phone:
      phoneChanged && channels.phone.trim() && !isValidPhoneNumber(channels.phone.trim()) ? 'Ce numéro n’est pas valide.' : '',
    email: channels.email.trim() && !isMail(channels.email.trim()) ? 'Cette adresse e-mail n’est pas valide.' : '',
  };
  const channelsValid = !channelErrors.phone && !channelErrors.email;
  const canSave =
    ready &&
    legalValid &&
    channelsValid &&
    (legalChanged ||
      categoryChanged ||
      installationsChanged ||
      costChanged ||
      phoneChanged ||
      emailChanged ||
      sizeChanged ||
      commercialChanged);

  const editField = (field) => (event) => setDraft((previous) => ({ ...previous, [field]: event.target.value }));
  const selectAgreement = (updated) => {
    const selected = updated.find((agreement) => agreement.checked);
    setAgreements((previous) => previous.map((agreement) => ({ ...agreement, checked: agreement.id === selected?.value })));
    setDraft((previous) => ({ ...previous, conv_collective: selected ? selected.value : null }));
  };
  const toggleInstallation = (code) =>
    setInstalled((previous) => (previous.includes(code) ? previous.filter((one) => one !== code) : [...previous, code]));
  const deleteBigAccount = () =>
    ApiDatabase.deleteCompanyBigAccount(
      { token, idCompany: companyId },
      () => {
        toast.success('Le grand compte a bien été supprimé');
        setBigAccount('');
        setRefreshCompanyBigAccount((previous) => !previous);
      },
      () => toast.error('Le grand compte n’a pas pu être retiré.'),
    );

  const save = async (storedJustificatif = null) => {
    setSaving(true);
    try {
      const overview = {
        ...(pendingFields ?? legalFields),
        ...(categoryChanged ? { pennylane_category_id: normalizePennylaneCategoryId(category) } : {}),
      };
      if (Object.keys(overview).length > 0) {
        await callApi(ApiDatabase.postUpdateCompanyOverview, {
          token,
          idCompany: companyId,
          companyInfo: overview,
          ...(storedJustificatif ? { justificatif: storedJustificatif } : {}),
        }).catch((error) => {
          const worded = apiErrorText(error, { language });
          if (worded) throw Object.assign(new Error(worded), { worded: true });
          throw error;
        });
      }
      if (installationsChanged) {
        await callApi(ApiDatabase.postCollectiveInstallationsCompany, { token, idCompany: companyId, codes: installed });
      }
      if (costChanged) {
        await callApi(ApiDatabase.putDisplayCostEstimate, { token, idCompany: companyId, displayCostEstimate: costEstimate });
      }
      if (phoneChanged) await call(crmApi.setCompanyPhone, { companyId, phone: channels.phone.trim() });
      if (emailChanged) {
        await call(crmApi.setCompanyEmail, { companyId, email: channels.email.trim() }).catch((error) => {
          throw Object.assign(new Error(apiErrorText(error, { language, text, group: 'emailErrors' })), { worded: true });
        });
      }
      if (sizeChanged) await call(crmApi.setCompanySize, { companyId, size });
      if (commercialChanged) {
        await call(crmApi.assignCompany, { companyId, role: 'commercial', personId: commercialId }).catch((error) => {
          throw Object.assign(new Error(apiErrorText(error, { language, text: companiesText, group: 'assignErrors' })), { worded: true });
        });
      }
      toast.success('L’entreprise a été mise à jour.');
      notifyRecord();
      notifyCompanies();
      close();
    } catch (err) {
      if (err?.name === 'CanceledError') return;
      console.error(err);
      toast.error(err?.worded ? err.message : (err?.response?.data?.message ?? 'L’entreprise n’a pas pu être mise à jour.'));
    } finally {
      setSaving(false);
    }
  };

  // The legal information identifies the company on its contracts: its
  // change carries the client's written request (JBWEB-1214).
  const submit = () => {
    if (justificatif.isConfirming) return justificatif.confirm(save);
    if (!canSave) return undefined;
    if (legalChanged) {
      setPendingFields(legalFields);
      return justificatif.setIsConfirming(true);
    }
    return save();
  };

  const selectedCategory = categories.find(
    (one) => normalizePennylaneCategoryId(one.id) === normalizePennylaneCategoryId(category),
  );
  const categoryOptions = [
    { value: null, label: 'Aucune catégorie', checked: normalizePennylaneCategoryId(category) === null },
    ...categories.map((one) => ({
      value: one.id,
      label: one.label,
      checked: normalizePennylaneCategoryId(one.id) === normalizePennylaneCategoryId(category),
    })),
  ];

  const form = ready ? (
    <div className="flex flex-col gap-8">
      {tab === 'legal' && (
      <Section>
        <StyledInput type="text" label="Raison sociale" value={draft.reason_social} onChange={editField('reason_social')} />
        <StyledInput type="text" label="Nom commercial" value={draft.name_commercial} onChange={editField('name_commercial')} />
        <div className="grid grid-cols-2 gap-4">
          <StyledInput
            type="phone"
            label="Numéro"
            value={channels.phone}
            onChange={(phone) => setChannels((previous) => ({ ...previous, phone }))}
            error={channelErrors.phone}
          />
          {/* The general e-mail is written by the agency manager and the sales reps only. */}
          <StyledInput
            type="email"
            label="E-mail"
            value={channels.email}
            isDisabled={!opened.canEditEmail}
            onChange={(event) => setChannels((previous) => ({ ...previous, email: event.target.value }))}
            error={channelErrors.email}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <StyledInput type="number" label="SIRET" value={draft.siret} onChange={editField('siret')} error={errors.errorSiret} />
          <StyledInput type="select" label="Forme juridique" value={draft.status} list={statusOptionsOf(company)} onChange={editField('status')} />
          <StyledInput type="text" label="Code APE" value={draft.code_ape} onChange={editField('code_ape')} error={errors.errorCodeApe} />
          <StyledInput type="number" label="Effectif" value={draft.staff} onChange={editField('staff')} error={errors.errorStaff} />
          <StyledInput
            type="number"
            label={text('fieldCapital')}
            unit="€"
            step="any"
            value={draft.capital}
            onChange={editField('capital')}
            error={errors.errorCapital}
          />
        </div>
        <StyledInput
          type="select"
          label="Fournisseur"
          value={draft.companySupplier || ''}
          list={buildCompanySupplierFormOptions(suppliers)}
          onChange={(event) => setDraft((previous) => ({ ...previous, companySupplier: event.target.value || null }))}
        />
        <div className="flex flex-col gap-1">
          <FieldLabel label="Adresse" />
          <AddressGeoApi
            addressGeoValue={(address) => setDraft((previous) => ({ ...previous, address }))}
            addressValue={draft.address}
            page="editCompany"
          />
        </div>
        <StyledInput
          type="selectMultipleRadio"
          label="Convention collective"
          value={agreements.find((agreement) => agreement.checked)?.name || '-'}
          list={agreements.map((agreement) => ({ value: agreement.id, label: agreement.name, checked: agreement.checked }))}
          onChange={selectAgreement}
          isSearchable
          dropdownFullWidth
        />
      </Section>
      )}

      {tab === 'activity' && (
      <Section>
        <StyledInput
          type="selectMultipleRadio"
          label="Catégorie"
          isSearchable
          value={normalizePennylaneCategoryId(category) === null ? 'Aucune catégorie' : selectedCategory?.label || '-'}
          list={categoryOptions}
          dropdownFullWidth
          onChange={(updated) => setCategory(normalizePennylaneCategoryId(updated.find((option) => option.checked)?.value ?? null))}
          footer={
            <div className="flex cursor-pointer items-center gap-1 p-1" onClick={() => toggle('company_pennylane_create_category')}>
              <Plus wh={20} color="#374151" />
              Créer une catégorie
            </div>
          }
        />
        <div className="flex flex-col gap-1">
          {bigAccount ? (
            <>
              <FieldLabel label="Grand compte" />
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <span className="text-sm font-medium text-gray-900">{bigAccount?.name}</span>
                <button type="button" aria-label="Retirer le grand compte" className={cardIconButtonClass} onClick={deleteBigAccount}>
                  <Close wh={18} color="#374151" />
                </button>
              </div>
            </>
          ) : (
            // The selector carries its own « Grand compte » label.
            <TextSelectMultipleBigAccount />
          )}
          <p className="text-xs text-gray-500">Le grand compte est enregistré dès sa sélection.</p>
        </div>
        <StyledInput
          type="select"
          label="Taille du client"
          value={size ?? ''}
          isDisabled={opened.sizeFromEtp}
          list={[
            { value: '', label: 'Non renseignée' },
            ...CLIENT_SIZES.map((one) => ({ value: one, label: clientSizeLabel(language, one) })),
          ]}
          onChange={(event) => setSize(event.target.value || null)}
        />
        {/* A client's size follows its timesheets: nothing to choose. */}
        {opened.sizeFromEtp && <p className="-mt-3 text-xs text-gray-500">Suit les relevés d’heures du client.</p>}
        <StyledInput
          type="select"
          label="Commercial"
          value={commercialId ?? ''}
          placeholder="Aucun commercial"
          list={commercials.map((person) => ({ value: person.id, label: `${person.firstname} ${person.lastname}`.trim() }))}
          onChange={(event) => setCommercialId(event.target.value || null)}
        />
      </Section>
      )}

      {tab === 'installations' && (
      <Section>
        <div className="min-w-0 overflow-hidden rounded-lg border border-gray-100 bg-white">
          <ul className="divide-y divide-gray-100">
            {referential.map((installation) => (
              <li key={installation.code} className="flex px-5 py-4 hover:bg-gray-50">
                <StyledInput
                  type="checkbox"
                  label={installation.label}
                  checked={installed.includes(installation.code)}
                  onChange={() => toggleInstallation(installation.code)}
                />
              </li>
            ))}
          </ul>
        </div>
      </Section>
      )}

      {tab === 'costs' && (
      <Section>
        <StyledInput
          type="checkbox"
          label="Le client voit les estimations de coût"
          checked={costEstimate}
          onChange={() => setCostEstimate((previous) => !previous)}
        />
      </Section>
      )}
    </div>
  ) : (
    // The legal tab's fields, at their place, until the company is read; the
    // other tabs, two fields.
    <div aria-busy="true" className="flex flex-col gap-4">
      {tab === 'legal' ? (
        <>
          <SkeletonField label="Raison sociale" />
          <SkeletonField label="Nom commercial" />
          <div className="grid grid-cols-2 gap-4">
            <SkeletonField label="Numéro" />
            <SkeletonField label="E-mail" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <SkeletonField label="SIRET" />
            <SkeletonField label="Forme juridique" />
            <SkeletonField label="Code APE" />
            <SkeletonField label="Effectif" />
            <SkeletonField label={text('fieldCapital')} />
          </div>
          <SkeletonField label="Fournisseur" />
          <SkeletonField label="Adresse" />
          <SkeletonField label="Convention collective" />
        </>
      ) : (
        <>
          <SkeletonField />
          <SkeletonField />
        </>
      )}
    </div>
  );

  return (
    <>
      <SlideOverHeader
        hide={close}
        closeLabel="Fermer"
        description="Informations légales, coordonnées, activité et suivi commercial de l’entreprise"
      >
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Modifier l’entreprise'}
      </SlideOverHeader>
      {/* The tabs, under the title; the confirmation step has none. */}
      {!justificatif.isConfirming && (
        <div className="flex-shrink-0 px-6 pb-3">
          <PillTabs
            wrap
            activeTab={tab}
            onChange={setTab}
            // A tab holding a field to correct says how many.
            tabs={TABS.map((one) => ({
              ...one,
              count:
                one.id === 'legal' && !legalValid
                  ? Object.values(errors).filter(Boolean).length
                  : undefined,
            }))}
          />
        </div>
      )}
      <SlideOverBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
            description="Ces informations identifient l'entreprise sur ses contrats. Un justificatif est requis avant validation."
          />
        ) : (
          form
        )}
      </SlideOverBody>
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          <Button
            size="SM"
            color="SECONDARY"
            // Backs out of the confirmation, not the panel.
            onClick={
              justificatif.isConfirming
                ? () => {
                    setPendingFields(null);
                    justificatif.setIsConfirming(false);
                  }
                : close
            }
          >
            Annuler
          </Button>
          {saving || justificatif.isUploading ? (
            <Button size="SM" color="DISABLED">
              Enregistrement…
            </Button>
          ) : (
            <Button
              size="SM"
              color={(justificatif.isConfirming ? justificatif.canConfirm : canSave) ? 'PRIMARY' : 'DISABLED'}
              disabled={!(justificatif.isConfirming ? justificatif.canConfirm : canSave)}
              onClick={submit}
            >
              Enregistrer
            </Button>
          )}
        </SlideOverFooter>
      </div>
    </>
  );
};

export default CompanyEditPanel;
